import type { GeneratedSegment } from "@keydials/api-client";
import type { SegmentNoun } from "@keydials/dial-workspace";
import type { BuildAction } from "./builderReducer";
import { ConfirmRemove } from "./ConfirmRemove";
import { builderCopy as copy, fill, nounValues } from "./copy";

interface ChecklistItemsProps {
  sectionKey: string;
  segment: GeneratedSegment;
  noun: SegmentNoun;
  /** The item just added, or chosen: an empty one takes the cursor. */
  selectedItemKey: string | null;
  disabled: boolean;
  confirming: string | null;
  setConfirming: (armed: string | null) => void;
  dispatch: (action: BuildAction) => void;
}

/**
 * The checklist inside an action point (KEY-560), on a dial with a checklist inside each. Each item has its name and whether it
 * can be given a review date, which is on unless the author turns it off (the server reads a missing value as on), and can be
 * moved or removed.
 */
export function ChecklistItems({ sectionKey, segment, noun, selectedItemKey, disabled, confirming, setConfirming, dispatch }: ChecklistItemsProps) {
  const segmentKey = segment.key;
  const items = segment.items;

  return (
    <div className="kd-checklist">
      <h3 className="kd-panel-heading">{fill(copy.build.itemsHeading, { ...nounValues(noun), n: items.length })}</h3>
      {items.length > 0 && (
        <ol className="kd-panel-list">
          {items.map((item, index) => {
            const armed = `item:${sectionKey}/${segmentKey}/${item.key}`;
            return (
              <li key={item.key} className="kd-checklist-item">
                <input
                  className="kd-checklist-name"
                  aria-label={`${copy.build.nameLabel} ${index + 1}`}
                  value={item.name}
                  placeholder={copy.build.untitled}
                  disabled={disabled}
                  autoFocus={item.key === selectedItemKey && !item.name}
                  onChange={(e) => dispatch({ type: "renameItem", sectionKey, segmentKey, itemKey: item.key, name: e.target.value })}
                />
                <div className="kd-checklist-controls">
                  <label className="kd-checklist-review">
                    <input
                      type="checkbox"
                      role="switch"
                      checked={item.allowsReviewDate !== false}
                      disabled={disabled}
                      onChange={(e) => dispatch({ type: "setItemReviewDate", sectionKey, segmentKey, itemKey: item.key, allows: e.target.checked })}
                    />
                    {copy.build.reviewDate}
                  </label>
                  <span className="kd-checklist-actions">
                    <button
                      type="button"
                      className="kd-icon-btn"
                      disabled={disabled || index === 0}
                      onClick={() => dispatch({ type: "moveItem", sectionKey, segmentKey, itemKey: item.key, delta: -1 })}
                    >
                      {copy.build.moveUp}
                    </button>
                    <button
                      type="button"
                      className="kd-icon-btn"
                      disabled={disabled || index === items.length - 1}
                      onClick={() => dispatch({ type: "moveItem", sectionKey, segmentKey, itemKey: item.key, delta: 1 })}
                    >
                      {copy.build.moveDown}
                    </button>
                    <button type="button" className="kd-icon-btn" disabled={disabled} onClick={() => setConfirming(armed)}>
                      {copy.build.remove}
                    </button>
                  </span>
                </div>
                {confirming === armed && (
                  <ConfirmRemove
                    name={item.name}
                    onKeep={() => setConfirming(null)}
                    onRemove={() => {
                      setConfirming(null);
                      dispatch({ type: "removeItem", sectionKey, segmentKey, itemKey: item.key });
                    }}
                  />
                )}
              </li>
            );
          })}
        </ol>
      )}
      <div className="kd-panel-actions">
        <button type="button" className="kd-btn" disabled={disabled} onClick={() => dispatch({ type: "addItem", sectionKey, segmentKey })}>
          {copy.build.addItem}
        </button>
      </div>
    </div>
  );
}
